export interface DeviceInfo {
  deviceType: string;
  os: string;
  browser: string;
  isInAppBrowser: boolean;
  inAppName?: string;
  language: string;
  timeZone: string;
  countryGuess: string;
  screenResolution: string;
  connectionType?: string;
  userAgent: string;
}

/**
 * Maps standard IANA timezones and locales to recognizable countries with flag emoji
 */
export function guessCountry(timeZone: string, locale: string): string {
  const tz = (timeZone || '').toLowerCase();
  const loc = (locale || '').toLowerCase();

  if (tz.includes('tokyo') || loc.includes('ja') || loc.includes('jp')) {
    return 'Japon 🇯🇵';
  }
  if (tz.includes('paris') || loc.startsWith('fr')) {
    return 'France 🇫🇷';
  }
  if (tz.includes('new_york') || tz.includes('los_angeles') || tz.includes('chicago') || tz.includes('america') || loc.includes('en-us')) {
    return 'États-Unis 🇺🇸';
  }
  if (tz.includes('london') || loc.includes('en-gb')) {
    return 'Royaume-Uni 🇬🇧';
  }
  if (tz.includes('sydney') || tz.includes('melbourne') || tz.includes('australia')) {
    return 'Australie 🇦🇺';
  }
  if (tz.includes('berlin') || loc.includes('de')) {
    return 'Allemagne 🇩🇪';
  }
  if (tz.includes('rome') || loc.includes('it')) {
    return 'Italie 🇮🇹';
  }
  if (tz.includes('madrid') || loc.includes('es')) {
    return 'Espagne 🇪🇸';
  }
  if (tz.includes('brussels')) {
    return 'Belgique 🇧🇪';
  }
  if (tz.includes('zurich') || tz.includes('geneva')) {
    return 'Suisse 🇨🇭';
  }
  if (tz.includes('toronto') || tz.includes('vancouver') || loc.includes('en-ca') || loc.includes('fr-ca')) {
    return 'Canada 🇨🇦';
  }
  if (tz.includes('dubai') || tz.includes('asia/dubai')) {
    return 'Émirats Arabes Unis 🇦🇪';
  }
  if (tz.includes('shanghai') || tz.includes('beijing') || loc.includes('zh')) {
    return 'Chine 🇨🇳';
  }
  if (tz.includes('seoul') || loc.includes('ko')) {
    return 'Corée du Sud 🇰🇷';
  }

  // Fallback with timezone name
  if (timeZone) {
    const parts = timeZone.split('/');
    return parts[parts.length - 1].replace('_', ' ') + ' 🌐';
  }

  return 'International 🌐';
}

/**
 * Comprehensive parser for Device, OS, Browser and In-App WebViews (LINE, Instagram, Facebook...)
 */
export function detectDevice(): DeviceInfo {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      deviceType: 'Serveur',
      os: 'Inconnu',
      browser: 'Inconnu',
      isInAppBrowser: false,
      language: 'fr-FR',
      timeZone: 'Europe/Paris',
      countryGuess: 'France 🇫🇷',
      screenResolution: '1920x1080',
      userAgent: 'Unknown'
    };
  }

  const ua = navigator.userAgent || '';
  const language = navigator.language || (navigator as any).userLanguage || 'fr-FR';
  let timeZone = 'Europe/Paris';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';
  } catch {
    // fallback
  }

  const countryGuess = guessCountry(timeZone, language);
  const screenResolution = `${window.screen?.width || window.innerWidth}x${window.screen?.height || window.innerHeight}`;

  // 1. Detect In-App Browsers (Crucial for Japan / Asia & Social sharing)
  let isInAppBrowser = false;
  let inAppName: string | undefined = undefined;

  if (/Line\//i.test(ua)) {
    isInAppBrowser = true;
    inAppName = 'LINE';
  } else if (/Instagram/i.test(ua)) {
    isInAppBrowser = true;
    inAppName = 'Instagram';
  } else if (/FBAN|FBAV|FB_IAB/i.test(ua)) {
    isInAppBrowser = true;
    inAppName = 'Facebook';
  } else if (/MicroMessenger/i.test(ua)) {
    isInAppBrowser = true;
    inAppName = 'WeChat';
  } else if (/musical_ly|ByteLocale|TikTok/i.test(ua)) {
    isInAppBrowser = true;
    inAppName = 'TikTok';
  } else if (/Twitter|X-App/i.test(ua)) {
    isInAppBrowser = true;
    inAppName = 'Twitter / X';
  } else if (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua) || /\bwv\b/i.test(ua)) {
    isInAppBrowser = true;
    inAppName = 'Navigateur Intégré (WebView)';
  }

  // 2. Detect OS
  let os = 'Inconnu';
  if (/iPhone/i.test(ua)) {
    const match = ua.match(/OS (\d+[_\.]\d+)/i);
    os = match ? `iOS ${match[1].replace('_', '.')}` : 'iOS (iPhone)';
  } else if (/iPad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    const match = ua.match(/OS (\d+[_\.]\d+)/i);
    os = match ? `iPadOS ${match[1].replace('_', '.')}` : 'iPadOS';
  } else if (/Android/i.test(ua)) {
    const match = ua.match(/Android (\d+(\.\d+)?)/i);
    os = match ? `Android ${match[1]}` : 'Android';
  } else if (/Macintosh|Mac OS X/i.test(ua)) {
    os = 'macOS (Mac)';
  } else if (/Windows NT/i.test(ua)) {
    if (ua.includes('Windows NT 10.0')) os = 'Windows 10 / 11';
    else if (ua.includes('Windows NT 6.3')) os = 'Windows 8.1';
    else if (ua.includes('Windows NT 6.1')) os = 'Windows 7';
    else os = 'Windows';
  } else if (/Linux/i.test(ua)) {
    os = 'Linux';
  }

  // 3. Detect Device Type
  let deviceType = 'Ordinateur (PC/Mac)';
  if (/iPhone/i.test(ua)) {
    deviceType = 'iPhone 📱';
  } else if (/iPad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    deviceType = 'iPad 📲';
  } else if (/Android/i.test(ua)) {
    deviceType = /Mobile/i.test(ua) ? 'Smartphone Android 📱' : 'Tablette Android 📲';
  } else if (/Macintosh|Mac OS X/i.test(ua)) {
    deviceType = 'MacBook / iMac 💻';
  } else if (/Windows/i.test(ua)) {
    deviceType = 'PC Windows 🖥️';
  }

  // 4. Detect Browser
  let browser = 'Navigateur Standard';
  if (inAppName) {
    browser = `In-App ${inAppName}`;
  } else if (/CriOS/i.test(ua)) {
    browser = 'Chrome iOS';
  } else if (/FxiOS/i.test(ua)) {
    browser = 'Firefox iOS';
  } else if (/EdgiOS|EdgA|Edge/i.test(ua)) {
    browser = 'Microsoft Edge';
  } else if (/Chrome/i.test(ua) && !/Chromium|Edge|Edg|OPR/i.test(ua)) {
    browser = /Mobile/i.test(ua) ? 'Chrome Mobile' : 'Chrome';
  } else if (/Safari/i.test(ua) && !/Chrome|Chromium|Edge|OPR/i.test(ua)) {
    browser = /Mobile/i.test(ua) || /iPhone|iPad/i.test(ua) ? 'Safari Mobile' : 'Safari';
  } else if (/Firefox/i.test(ua)) {
    browser = 'Firefox';
  } else if (/OPR|Opera/i.test(ua)) {
    browser = 'Opera';
  }

  // Connection info if available
  let connectionType: string | undefined = undefined;
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  if (connection) {
    connectionType = connection.effectiveType || (connection.saveData ? 'Économie de données' : undefined);
  }

  return {
    deviceType,
    os,
    browser,
    isInAppBrowser,
    inAppName,
    language,
    timeZone,
    countryGuess,
    screenResolution,
    connectionType,
    userAgent: ua
  };
}
